<div
  class="fi-trigger"
  tabindex="0"
  role="button"
  matTooltip="Open the OTA flow diagrams and glossary"
  aria-label="How OTA Work"
  (click)="open()"
  (keydown.enter)="open()"
>
  <mat-icon class="fi-trigger-icon">menu_book</mat-icon>
</div>

<div
  class="custom-container ota-info-root info-panel-root"
  [class.active]="isOpen"
>
  <div class="custom-modal custom-modal-l">
    <div class="custom-modal-dialog">
      <div class="custom-modal-content">
        <div class="custom-modal-header">
          <h4 class="custom-modal-title">How OTA Work</h4>
          <div class="fi-header-actions">
            <button
              type="button"
              class="fi-export-btn"
              (click)="exportPdf()"
              aria-label="Export as PDF"
            >
              <mat-icon>picture_as_pdf</mat-icon>
              Export as PDF
            </button>
            <button type="button" class="custom-close-btn" (click)="close()">
              &times;
            </button>
          </div>
        </div>

        <div class="custom-modal-body fi-body">
          <p class="fi-intro">
            OTA here means a one-off GET/SET/CLR device parameter command
            &mdash; completely unrelated to firmware (that's FOTA,
            documented separately). This traces how a command gets built,
            queued, sent to a device, and resolved to
            <span class="fi-pill fi-pill-ok">Completed</span> or
            <span class="fi-pill fi-pill-pause">Mark As Completed</span>,
            plus how it relates to FOTA.
          </p>

          <section class="fi-section">
            <h5 class="fi-h5">1. From click to queued command</h5>
            <p class="fi-p">
              Four different screens all end up building the exact same
              two-record shape (a device row plus one command row per
              command) through one shared function &mdash; the only real
              difference between them is where the device list and command
              values come from.
            </p>

            <figure class="fi-figure">
              <svg
                viewBox="0 0 800 520"
                xmlns="http://www.w3.org/2000/svg"
                role="img"
                aria-label="Four entry points - Manual OTA, and three batch creation modes - all feeding into one shared creation pipeline that draws command templates from the OTA Master catalog, validates each device, and inserts either a pending or a pre-aborted row."
              >
                <defs>
                  <marker id="oArrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6.5" markerHeight="6.5" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="currentColor"/>
                  </marker>
                  <marker id="oArrowDanger" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6.5" markerHeight="6.5" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="var(--info-danger)"/>
                  </marker>
                  <marker id="oArrowOk" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6.5" markerHeight="6.5" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="var(--info-ok)"/>
                  </marker>
                  <marker id="oArrowMuted" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6.5" markerHeight="6.5" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="var(--theme-text-muted)"/>
                  </marker>
                </defs>

                <rect x="10" y="10" width="170" height="50" rx="9" fill="var(--theme-surface-alt)" stroke="var(--theme-border)"/>
                <text x="95" y="31" text-anchor="middle" font-size="8.2" font-weight="600" fill="var(--theme-text)">Manual OTA</text>
                <text x="95" y="46" text-anchor="middle" font-size="7.4" fill="var(--theme-text-muted)">1 IMEI, no batch header</text>

                <rect x="196" y="10" width="170" height="50" rx="9" fill="var(--theme-surface-alt)" stroke="var(--theme-border)"/>
                <text x="281" y="31" text-anchor="middle" font-size="8.2" font-weight="600" fill="var(--theme-text)">Batch: pick devices</text>
                <text x="281" y="46" text-anchor="middle" font-size="7.4" fill="var(--theme-text-muted)">hand-picked list, N commands</text>

                <rect x="382" y="10" width="170" height="50" rx="9" fill="var(--theme-surface-alt)" stroke="var(--theme-border)"/>
                <text x="467" y="31" text-anchor="middle" font-size="8.2" font-weight="600" fill="var(--theme-text)">Batch: bulk CSV</text>
                <text x="467" y="46" text-anchor="middle" font-size="7.4" fill="var(--theme-text-muted)">IMEI list, same N commands</text>

                <rect x="568" y="10" width="182" height="50" rx="9" fill="var(--theme-surface-alt)" stroke="var(--theme-border)"/>
                <text x="659" y="31" text-anchor="middle" font-size="8.2" font-weight="600" fill="var(--theme-text)">Batch: custom CSV</text>
                <text x="659" y="46" text-anchor="middle" font-size="7.4" fill="var(--theme-text-muted)">IMEI+value pairs, 1 command</text>

                <line x1="95" y1="60" x2="95" y2="90" stroke="currentColor" stroke-width="1.3"/>
                <line x1="281" y1="60" x2="281" y2="90" stroke="currentColor" stroke-width="1.3"/>
                <line x1="467" y1="60" x2="467" y2="90" stroke="currentColor" stroke-width="1.3"/>
                <line x1="659" y1="60" x2="659" y2="90" stroke="currentColor" stroke-width="1.3"/>
                <line x1="95" y1="90" x2="659" y2="90" stroke="currentColor" stroke-width="1.3"/>
                <line x1="380" y1="90" x2="380" y2="103" stroke="currentColor" stroke-width="1.5" marker-end="url(#oArrow)"/>

                <rect x="230" y="105" width="300" height="46" rx="9" fill="var(--info-accent-fill)" stroke="var(--info-accent)" stroke-width="1.6"/>
                <text x="380" y="123" text-anchor="middle" font-size="9.0" font-weight="700" fill="var(--info-accent-strong)">Shared creation pipeline</text>
                <text x="380" y="139" text-anchor="middle" font-size="7.4" fill="var(--info-accent-strong)">same validation &amp; setup, every mode</text>

                <path d="M170,177 L170,128 L228,128" fill="none" stroke="var(--theme-text-muted)" stroke-width="1.3" stroke-dasharray="3 3" marker-end="url(#oArrowMuted)"/>
                <rect x="20" y="177" width="150" height="42" rx="8" fill="var(--theme-surface)" stroke="var(--theme-border)"/>
                <text x="95" y="195" text-anchor="middle" font-size="7.8" font-weight="600" fill="var(--theme-text)">OTA Master catalog</text>
                <text x="95" y="209" text-anchor="middle" font-size="7.0" fill="var(--theme-text-muted)">picks 1+ command templates</text>

                <line x1="380" y1="151" x2="380" y2="238" stroke="currentColor" stroke-width="1.5" marker-end="url(#oArrow)"/>

                <polygon points="380,240 470,282 380,324 290,282" fill="var(--theme-surface)" stroke="var(--theme-border)"/>
                <text x="380" y="277" text-anchor="middle" font-size="7.8" fill="var(--theme-text)">IMEI valid &amp;</text>
                <text x="380" y="290" text-anchor="middle" font-size="7.8" fill="var(--theme-text)">device found?</text>

                <line x1="290" y1="282" x2="230" y2="282" stroke="var(--info-danger)" stroke-width="1.5" marker-end="url(#oArrowDanger)"/>
                <text x="260" y="273" text-anchor="middle" font-size="7.4" fill="var(--info-danger)">no</text>
                <rect x="20" y="257" width="210" height="50" rx="9" fill="var(--info-danger-fill)" stroke="var(--info-danger)"/>
                <text x="125" y="277" text-anchor="middle" font-size="7.8" font-weight="700" fill="var(--info-danger)">Row inserted already Aborted</text>
                <text x="125" y="292" text-anchor="middle" font-size="7.0" fill="var(--info-danger)">no dispatch is ever attempted</text>

                <line x1="380" y1="324" x2="380" y2="347" stroke="currentColor" stroke-width="1.5" marker-end="url(#oArrow)"/>
                <text x="396" y="340" font-size="7.4" fill="var(--theme-text-muted)">yes</text>

                <rect x="200" y="350" width="360" height="56" rx="9" fill="var(--theme-surface-alt)" stroke="var(--theme-border)"/>
                <text x="380" y="371" text-anchor="middle" font-size="8.6" font-weight="600" fill="var(--theme-text)">Record it as Pending, straight away</text>
                <text x="380" y="386" text-anchor="middle" font-size="7.4" fill="var(--theme-text-muted)">one device entry, plus one command entry</text>
                <text x="380" y="399" text-anchor="middle" font-size="7.4" fill="var(--theme-text-muted)">per command &mdash; no separate "Start OTA" step</text>

                <line x1="380" y1="406" x2="380" y2="429" stroke="var(--info-ok)" stroke-width="1.5" marker-end="url(#oArrowOk)"/>

                <rect x="230" y="432" width="300" height="46" rx="9" fill="var(--info-ok-fill)" stroke="var(--info-ok)" stroke-width="1.6"/>
                <text x="380" y="450" text-anchor="middle" font-size="8.6" font-weight="700" fill="var(--info-ok)">Picked up on the device's very next login</text>
                <text x="380" y="465" text-anchor="middle" font-size="7.4" fill="var(--info-ok)">see Section 2 below</text>

                <text x="650" y="270" text-anchor="middle" font-size="7.4" font-weight="700" fill="var(--theme-text-muted)" letter-spacing="0.02em">RECORD HIERARCHY</text>
                <rect x="600" y="280" width="150" height="34" rx="6" fill="var(--theme-surface)" stroke="var(--theme-border)"/>
                <text x="675" y="301" text-anchor="middle" font-size="7.4" fill="var(--theme-text)">Batch record (1)</text>
                <line x1="675" y1="314" x2="675" y2="322" stroke="var(--theme-text-muted)" stroke-width="1.2"/>
                <rect x="600" y="324" width="150" height="34" rx="6" fill="var(--theme-surface)" stroke="var(--theme-border)"/>
                <text x="675" y="345" text-anchor="middle" font-size="7.4" fill="var(--theme-text)">Device record (N)</text>
                <line x1="675" y1="358" x2="675" y2="366" stroke="var(--theme-text-muted)" stroke-width="1.2"/>
                <rect x="600" y="368" width="150" height="34" rx="6" fill="var(--theme-surface)" stroke="var(--theme-border)"/>
                <text x="675" y="389" text-anchor="middle" font-size="7.4" fill="var(--theme-text)">Command record (N &times; cmds)</text>
                <text x="675" y="415" text-anchor="middle" font-size="6.6" fill="var(--theme-text-muted)">Manual OTA skips the top tier</text>
                <text x="675" y="427" text-anchor="middle" font-size="6.6" fill="var(--theme-text-muted)">(no batch header at all)</text>
              </svg>
              <figcaption class="fi-caption">
                Invalid rows (bad IMEI format, or IMEI not in the device
                master) never even reach the eligibility check on the
                device server &mdash; they're written straight to
                <code>Aborted</code> at creation time. Unlike FOTA, there's
                no draft/&ldquo;Not-Started&rdquo; state and no admin has to
                press a separate Start button; every valid row is
                immediately live.
              </figcaption>
            </figure>

            <ul class="fi-rule-list">
              <li>
                <strong>OTA Master</strong> is the reusable command catalog
                (name, raw command template,
                <code>GET</code>/<code>SET</code>/<code>CLR</code> type,
                whether it needs an operator-supplied value, a response
                timeout). Once any device has ever received a given
                template, that template can no longer be edited or deleted.
              </li>
              <li>
                <strong>&ldquo;Manual OTA&rdquo; vs. batch &ldquo;manual&rdquo; mode</strong>
                &mdash; a naming collision worth knowing about: the
                single-IMEI page is called Manual OTA, while one of the
                three ways to build a <em>batch</em> is also labeled
                &ldquo;manual&rdquo; in the create-batch dropdown (meaning
                &ldquo;I hand-picked these devices,&rdquo; not &ldquo;this
                skips the batch system&rdquo;). They're different features.
              </li>
              <li>
                <strong>Custom mode is the odd one out</strong> &mdash;
                every other mode applies the same set of commands to every
                device in the list; custom mode allows exactly one command,
                with its value read per-device from the uploaded CSV
                instead.
              </li>
            </ul>
          </section>

          <section class="fi-section">
            <h5 class="fi-h5">2. What happens once a device has pending commands</h5>
            <p class="fi-p">
              Everything below runs inside the device's regular check-in
              connection to the TCP server &mdash; not the admin backend. A
              device only ever finds out about its commands by logging in.
            </p>

            <figure class="fi-figure">
              <svg
                viewBox="0 0 760 1020"
                xmlns="http://www.w3.org/2000/svg"
                role="img"
                aria-label="Flowchart of a device's OTA execution: checking for pending commands ahead of FOTA, sending every pending command in one pass over the same connection, then two independent completion paths - a matched device reply, or a timeout - both of which count as success, plus a separate admin-only abort path."
              >
                <defs>
                  <marker id="oArrow2" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6.5" markerHeight="6.5" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="currentColor"/>
                  </marker>
                  <marker id="oArrowOk2" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6.5" markerHeight="6.5" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="var(--info-ok)"/>
                  </marker>
                  <marker id="oArrowPause" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6.5" markerHeight="6.5" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="var(--info-pause)"/>
                  </marker>
                  <marker id="oArrowDanger2" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6.5" markerHeight="6.5" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="var(--info-danger)"/>
                  </marker>
                </defs>

                <rect x="170" y="10" width="260" height="46" rx="9" fill="var(--theme-surface-alt)" stroke="var(--theme-border)"/>
                <text x="300" y="38" text-anchor="middle" font-size="9.8" font-weight="600" fill="var(--theme-text)">Device sends a login packet</text>
                <line x1="300" y1="56" x2="300" y2="78" stroke="currentColor" stroke-width="1.5" marker-end="url(#oArrow2)"/>

                <polygon points="300,80 430,130 300,180 170,130" fill="var(--theme-surface)" stroke="var(--theme-border)"/>
                <text x="300" y="121" text-anchor="middle" font-size="8.2" fill="var(--theme-text)">Pending OTA record</text>
                <text x="300" y="134" text-anchor="middle" font-size="8.2" fill="var(--theme-text)">for this IMEI?</text>
                <text x="300" y="147" text-anchor="middle" font-size="6.6" fill="var(--theme-text-muted)">(oldest record first)</text>

                <line x1="430" y1="130" x2="478" y2="130" stroke="currentColor" stroke-width="1.5" marker-end="url(#oArrow2)"/>
                <text x="454" y="121" text-anchor="middle" font-size="7.8" fill="var(--theme-text-muted)">no</text>
                <rect x="480" y="105" width="220" height="50" rx="8" fill="var(--theme-surface-alt)" stroke="var(--theme-border)"/>
                <text x="590" y="125" text-anchor="middle" font-size="8.6" fill="var(--theme-text)">Check FOTA instead</text>
                <text x="590" y="140" text-anchor="middle" font-size="7.8" fill="var(--theme-text-muted)">OTA always goes first</text>

                <line x1="300" y1="180" x2="300" y2="203" stroke="currentColor" stroke-width="1.5" marker-end="url(#oArrow2)"/>
                <text x="316" y="196" font-size="7.8" fill="var(--theme-text-muted)">yes</text>

                <rect x="170" y="205" width="260" height="44" rx="8" fill="var(--theme-surface-alt)" stroke="var(--theme-border)"/>
                <text x="300" y="223" text-anchor="middle" font-size="8.6" font-weight="600" fill="var(--theme-text)">Lock this IMEI</text>
                <text x="300" y="238" text-anchor="middle" font-size="7.4" fill="var(--theme-text-muted)">a second login can't re-run it mid-way</text>

                <line x1="300" y1="249" x2="300" y2="271" stroke="currentColor" stroke-width="1.5" marker-end="url(#oArrow2)"/>

                <rect x="150" y="274" width="300" height="72" rx="9" fill="var(--theme-surface-alt)" stroke="var(--theme-border)"/>
                <text x="300" y="294" text-anchor="middle" font-size="8.6" font-weight="600" fill="var(--theme-text)">Send every Pending command, oldest first</text>
                <text x="300" y="310" text-anchor="middle" font-size="7.4" fill="var(--theme-text-muted)">one pass, same connection, literal command text</text>
                <text x="300" y="324" text-anchor="middle" font-size="7.4" fill="var(--theme-text-muted)">each: mark Triggered, arm a 60s watchdog,</text>
                <text x="300" y="338" text-anchor="middle" font-size="7.4" fill="var(--theme-text-muted)">then wait the command's own pacing delay</text>

                <line x1="300" y1="346" x2="300" y2="369" stroke="currentColor" stroke-width="1.5" marker-end="url(#oArrow2)"/>

                <rect x="170" y="372" width="260" height="44" rx="8" fill="var(--theme-surface-alt)" stroke="var(--theme-border)"/>
                <text x="300" y="390" text-anchor="middle" font-size="8.6" font-weight="600" fill="var(--theme-text)">Send final ack, close connection</text>
                <text x="300" y="405" text-anchor="middle" font-size="7.4" fill="var(--theme-text-muted)">unlike FOTA, no per-command disconnect</text>

                <text x="300" y="450" text-anchor="middle" font-size="8.2" font-weight="700" fill="var(--theme-text-muted)" letter-spacing="0.02em">MEANWHILE &mdash; HOW EACH "TRIGGERED" COMMAND RESOLVES</text>

                <rect x="60" y="470" width="300" height="46" rx="9" fill="var(--theme-surface-alt)" stroke="var(--theme-border)"/>
                <text x="210" y="489" text-anchor="middle" font-size="8.2" font-weight="600" fill="var(--theme-text)">Device replies STATUS# / PU: / EU:</text>
                <text x="210" y="504" text-anchor="middle" font-size="7.4" fill="var(--theme-text-muted)">maybe on a brand-new connection</text>

                <line x1="210" y1="516" x2="210" y2="539" stroke="currentColor" stroke-width="1.5" marker-end="url(#oArrow2)"/>

                <polygon points="210,541 320,574 210,607 100,574" fill="var(--theme-surface)" stroke="var(--theme-border)"/>
                <text x="210" y="570" text-anchor="middle" font-size="7.4" fill="var(--theme-text)">reply text matches a</text>
                <text x="210" y="583" text-anchor="middle" font-size="7.4" fill="var(--theme-text)">Triggered command?</text>

                <line x1="320" y1="574" x2="360" y2="574" stroke="var(--theme-text-muted)" stroke-width="1.3" marker-end="url(#oArrowMuted)"/>
                <text x="340" y="565" text-anchor="middle" font-size="7.0" fill="var(--theme-text-muted)">no</text>
                <text x="362" y="578" font-size="7.0" fill="var(--theme-text-muted)">ignored, logged only</text>

                <line x1="210" y1="607" x2="210" y2="630" stroke="var(--info-ok)" stroke-width="1.5" marker-end="url(#oArrowOk2)"/>
                <text x="226" y="622" font-size="7.4" fill="var(--info-ok)">yes</text>

                <rect x="80" y="633" width="260" height="44" rx="9" fill="var(--info-ok-fill)" stroke="var(--info-ok)" stroke-width="1.6"/>
                <text x="210" y="652" text-anchor="middle" font-size="9.0" font-weight="700" fill="var(--info-ok)">Completed</text>
                <text x="210" y="667" text-anchor="middle" font-size="7.4" fill="var(--info-ok)">device-confirmed</text>

                <rect x="420" y="470" width="300" height="60" rx="9" fill="var(--theme-surface-alt)" stroke="var(--theme-border)"/>
                <text x="570" y="488" text-anchor="middle" font-size="8.2" font-weight="600" fill="var(--theme-text)">No matching reply arrives</text>
                <text x="570" y="503" text-anchor="middle" font-size="7.4" fill="var(--theme-text-muted)">60s watchdog (device server) or</text>
                <text x="570" y="517" text-anchor="middle" font-size="7.4" fill="var(--theme-text-muted)">5-min sweep (backend cron, every 5 min)</text>

                <line x1="570" y1="530" x2="570" y2="630" stroke="var(--info-pause)" stroke-width="1.5" marker-end="url(#oArrowPause)"/>

                <rect x="440" y="633" width="260" height="44" rx="9" fill="var(--info-pause-fill)" stroke="var(--info-pause)" stroke-width="1.6"/>
                <text x="570" y="652" text-anchor="middle" font-size="9.0" font-weight="700" fill="var(--info-pause)">Mark As Completed</text>
                <text x="570" y="667" text-anchor="middle" font-size="7.4" fill="var(--info-pause)">timed out, not confirmed</text>

                <path d="M210,677 L210,700 L300,700" fill="none" stroke="var(--info-ok)" stroke-width="1.4"/>
                <path d="M570,677 L570,700 L470,700" fill="none" stroke="var(--info-pause)" stroke-width="1.4"/>

                <rect x="180" y="700" width="400" height="60" rx="10" fill="var(--theme-surface-alt)" stroke="var(--theme-border)"/>
                <text x="380" y="720" text-anchor="middle" font-size="8.6" font-weight="600" fill="var(--theme-text)">Both count as success &mdash; recompute rollups</text>
                <text x="380" y="736" text-anchor="middle" font-size="7.4" fill="var(--theme-text-muted)">device Completed once all its commands resolve;</text>
                <text x="380" y="750" text-anchor="middle" font-size="7.4" fill="var(--theme-text-muted)">batch Completed once all its devices resolve</text>

                <text x="380" y="805" text-anchor="middle" font-size="8.2" font-weight="700" fill="var(--info-danger)" letter-spacing="0.03em">ABORT IS ADMIN-ONLY &mdash; NOTHING ABOVE EVER ABORTS ITSELF</text>

                <rect x="150" y="825" width="220" height="50" rx="9" fill="var(--info-danger-fill)" stroke="var(--info-danger)"/>
                <text x="260" y="845" text-anchor="middle" font-size="8.2" font-weight="600" fill="var(--info-danger)">Admin aborts one device</text>
                <text x="260" y="860" text-anchor="middle" font-size="7.0" fill="var(--info-danger)">refused if already Completed</text>

                <rect x="390" y="825" width="220" height="50" rx="9" fill="var(--info-danger-fill)" stroke="var(--info-danger)"/>
                <text x="500" y="845" text-anchor="middle" font-size="8.2" font-weight="600" fill="var(--info-danger)">Admin aborts whole batch</text>
                <text x="500" y="860" text-anchor="middle" font-size="7.0" fill="var(--info-danger)">no-op if already Completed/Aborted</text>

                <line x1="260" y1="875" x2="260" y2="895" stroke="var(--info-danger)" stroke-width="1.3"/>
                <line x1="500" y1="875" x2="500" y2="895" stroke="var(--info-danger)" stroke-width="1.3"/>
                <path d="M260,895 L260,905 L500,905 L500,895" fill="none" stroke="var(--info-danger)" stroke-width="1.3"/>
                <line x1="380" y1="905" x2="380" y2="918" stroke="var(--info-danger)" stroke-width="1.4" marker-end="url(#oArrowDanger2)"/>

                <rect x="230" y="920" width="300" height="50" rx="10" fill="var(--info-danger-fill)" stroke="var(--info-danger)" stroke-width="1.6"/>
                <text x="380" y="940" text-anchor="middle" font-size="9.4" font-weight="700" fill="var(--info-danger)">Aborted</text>
                <text x="380" y="956" text-anchor="middle" font-size="7.4" fill="var(--info-danger)">every remaining Pending/Triggered row force-closed</text>
              </svg>
              <figcaption class="fi-caption">
                The whole send-loop above runs on one connection, one pass,
                oldest command first &mdash; a big contrast with FOTA,
                which disconnects and forces a fresh login after every
                single IP/state step. <strong>Completed</strong> means the
                device actually echoed back a recognizable confirmation;
                <strong>Mark As Completed</strong> means nobody ever heard
                back and the system gave up waiting &mdash; despite the
                name, it is not an operator action. There is no retry and
                no automatic abort anywhere in this pipeline; a command
                that never gets a reply simply times out into Mark As
                Completed exactly once.
              </figcaption>
            </figure>
          </section>

          <section class="fi-section">
            <h5 class="fi-h5">3. Terms you'll see in the batch/device list</h5>
            <div class="fi-glossary">
              <div class="fi-term">
                <dt>Completed vs. Mark As Completed</dt>
                <dd>
                  Both are treated as success everywhere (KPIs, rollup
                  counts). <code>Completed</code> is a real device
                  acknowledgment; <code>Mark As Completed</code> is a
                  timeout giving up on ever getting one. If you're auditing
                  whether a command actually took effect on the vehicle,
                  that distinction is the one to check.
                </dd>
              </div>
              <div class="fi-term">
                <dt>Three independent status levels</dt>
                <dd>
                  The batch, each device in it, and each command for that
                  device all carry their own separate status field. A
                  batch only reaches <code>Completed</code> once every
                  device under it is Completed or Aborted; a device only
                  reaches Completed once every command under it has
                  resolved.
                </dd>
              </div>
              <div class="fi-term">
                <dt>There's no separate &ldquo;is it online&rdquo; check</dt>
                <dd>
                  The server never polls or tracks which devices are
                  currently connected, and never reaches out to push a
                  command on its own. A device logging in <em>is</em> the
                  connectivity check &mdash; that login is the only moment
                  commands are ever looked up and sent, on that same
                  connection. A per-device lock only prevents two logins
                  for the same device from being processed at once; it
                  isn't a connection registry.
                </dd>
              </div>
            </div>
          </section>

          <div class="fi-callout">
            <strong>Relationship with FOTA:</strong> OTA and FOTA are fully
            independent &mdash; no shared validation, no shared
            eligibility checks, and the ticketing tool's AIS140 automation
            only ever creates FOTA batches, never OTA batches. The
            <em>only</em> place they interact is a device-server scheduling
            choice: on every login packet, the device asks "do I have a
            pending OTA command?" before it ever asks "do I have a pending
            FOTA assignment?" &mdash; so a device with any queued OTA
            command has its FOTA checked only after OTA is fully handled
            for that login.
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
